import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { FlexColumn, FlexRow, Padding } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { usePushNotifications } from '@/capacitor/hooks/usePushNotifications'
import { openAppSettings } from '@/capacitor/utils/app-settings'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { mixpanel } from '@/mixpanel'
import { ToggleSwitch } from '@/modules/shared/components/toggle-switch/ToggleSwitch'
import { useTranslated } from '@/utils/translation'
import { PageLink } from '../../components/page-link/PageLink'

const ProfilePrivacyPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()

  const { permissionGranted, requestPermissions } = usePushNotifications()
  const termsConditionsUrl = useTranslated('terms-conditions.url')
  const privacyPolicyUrl = useTranslated('privacy-policy.url')

  const handlePermissionsChanged = async () => {
    if (Capacitor.isNativePlatform()) {
      mixpanel.track('push_notification_enable_start', { origin: 'profile' })

      if (!permissionGranted) {
        await requestPermissions()
      }

      await openAppSettings()
    }
  }

  // Render Methods
  // ------------------------------------------------------------------------- /
  return (
    <ContentPageLayout
      noScroll
      header={
        <AppHeader
          primaryAction={{
            label: i18n._(globalDefinitions.goBack),
            icon: ['far', 'chevron-left'],
            onClick: () => {
              navigate('/profile', { replace: true })
            },
          }}
        />
      }
    >
      <Padding pt={theme.UI.Spacing.Space4}>
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <H3 noMargin>
            {t({
              id: 'profile.account-settings.privacy',
              comment: 'Privacy',
              message: 'Privacy',
            })}
          </H3>

          {Capacitor.isNativePlatform() ? (
            <>
              <Padding py={theme.UI.Spacing.Space4} onClick={handlePermissionsChanged}>
                <FlexRow justifyContent='space-between'>
                  <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darkest}>
                    {t({
                      id: 'profile.account-settings.notifications',
                      comment: 'Toggle Notifications',
                      message: 'Pushmeldingen',
                    })}
                  </Span>

                  {Capacitor.getPlatform() === 'android' ? (
                    <FontAwesomeIcon icon={['fal', 'external-link']} color={theme.Colors.Neutrals.Darkest} />
                  ) : (
                    <ToggleSwitch checked={permissionGranted} onChange={handlePermissionsChanged} />
                  )}
                </FlexRow>
              </Padding>
              <Line />
            </>
          ) : null}

          {privacyPolicyUrl ? (
            <>
              <PageLink icon={['fal', 'external-link']} external to={privacyPolicyUrl}>
                {t({
                  id: 'profile.account-settings.privacy-policy',
                  comment: 'Privacy Policy',
                  message: 'Privacybeleid',
                })}
              </PageLink>
              <Line />
            </>
          ) : null}

          {termsConditionsUrl && (
            <>
              <PageLink icon={['fal', 'external-link']} external to={termsConditionsUrl}>
                {t({
                  id: 'profile.account-settings.terms-conditions',
                  comment: 'Terms & Conditions',
                  message: 'Gebruiksvoorwaarden',
                })}
              </PageLink>
              <Line />
            </>
          )}
        </FlexColumn>
      </Padding>
    </ContentPageLayout>
  )
}

export { ProfilePrivacyPage }
